import * as THREE from "three";
import { BUILDING_DEFS, type BuildingType } from "@tribez/shared";

export interface PlacementPreview {
  start(type: BuildingType): void;
  cancel(): void;
  active(): boolean;
  /** Update under-cursor preview position. Returns the world point or null if off-terrain. */
  hover(raycaster: THREE.Raycaster, terrain: THREE.Mesh, sampleHeight: (x: number, z: number) => number): { x: number; z: number } | null;
  /** Confirm at last hover point. Returns {type, x, z} or null. */
  confirm(): { type: BuildingType; x: number; z: number } | null;
  /** Color the outline: green (ok) or red (rejected). Caller computes placement validity. */
  setValid(ok: boolean): void;
}

/** Top-center DOM overlay showing the building being placed + its cost.
 *  Hidden until placement starts; toggled by start/cancel/confirm. */
function createPlacementHud(): {
  show(name: string, cost: string): void;
  hide(): void;
} {
  const root = document.createElement("div");
  root.id = "placement-hud";
  root.style.display = "none";
  document.body.appendChild(root);
  return {
    show(name, cost) {
      root.innerHTML = `<span class="placement-hud__name">${name}</span><span class="placement-hud__cost">${cost}</span><span class="placement-hud__hint">click to place · Esc to cancel</span>`;
      root.style.display = "flex";
    },
    hide() { root.style.display = "none"; },
  };
}

function fmtBuildingCost(type: BuildingType): string {
  const c = BUILDING_DEFS[type].cost;
  const parts: string[] = [];
  if (c.lumber)         parts.push(`${c.lumber} Lumber`);
  if (c.stone)          parts.push(`${c.stone} Stone`);
  if (c.ironOre)        parts.push(`${c.ironOre} Iron`);
  if (c.woolOrLeather)  parts.push(`${c.woolOrLeather} Wool/Leather`);
  return parts.join(", ") || "free";
}

export function createPlacementPreview(scene: THREE.Scene): PlacementPreview {
  let activeType: BuildingType | null = null;
  let lastPos: { x: number; z: number } | null = null;
  const hud = createPlacementHud();

  const previewGroup = new THREE.Group();
  previewGroup.name = "placement-preview";
  previewGroup.visible = false;
  scene.add(previewGroup);

  const outline = new THREE.Mesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({ color: 0x66ff66, transparent: true, opacity: 0.4, side: THREE.DoubleSide, depthWrite: false, depthTest: false }),
  );
  outline.rotation.x = -Math.PI / 2;
  outline.renderOrder = 999;
  previewGroup.add(outline);

  function rebuildOutline(type: BuildingType) {
    const fp = BUILDING_DEFS[type].footprint;
    outline.geometry.dispose();
    outline.geometry = new THREE.PlaneGeometry(fp.cellsX, fp.cellsZ);
  }

  function start(type: BuildingType): void {
    activeType = type;
    rebuildOutline(type);
    previewGroup.visible = true;
    hud.show(type, fmtBuildingCost(type));
  }
  function cancel(): void {
    activeType = null;
    lastPos = null;
    previewGroup.visible = false;
    hud.hide();
  }
  function active(): boolean { return activeType !== null; }

  function hover(raycaster: THREE.Raycaster, terrain: THREE.Mesh, sampleHeight: (x: number, z: number) => number) {
    if (!activeType) return null;
    const hits = raycaster.intersectObject(terrain, false);
    if (hits.length === 0) return null;
    const p = hits[0]!.point;
    const x = Math.round(p.x);
    const z = Math.round(p.z);
    lastPos = { x, z };
    outline.position.set(x, sampleHeight(x, z) + 0.5, z);
    return lastPos;
  }

  function confirm() {
    if (!activeType || !lastPos) return null;
    const out = { type: activeType, x: lastPos.x, z: lastPos.z };
    cancel();
    return out;
  }

  function setValid(ok: boolean): void {
    (outline.material as THREE.MeshBasicMaterial).color.setHex(ok ? 0x66ff66 : 0xff6666);
  }

  return { start, cancel, active, hover, confirm, setValid };
}
